'use client';

import {
  Box,
  Divider,
  Drawer,
  DrawerBody,
  type DrawerFooterProps,
  DrawerHeader,
  Grid,
  Loader,
  Modal,
  ModalBody,
  ModalFooter,
  type ModalFooterProps,
  Typography
} from '@coolblue-development/freddy';
import addItemTrigger from '@coolblue-development/ga4-events/triggers/CrossSellAlternatives/addItemTrigger';
import closeOverlayTrigger from '@coolblue-development/ga4-events/triggers/CrossSellAlternatives/closeOverlayTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { useFreddyTranslations } from '@coolblue-development/next-design-system-utils/client';
import { SelectableBoxGroup } from '@coolblue-development/next-patterns/client';
import styled from '@emotion/styled';
import { SecondChanceProduct } from '@features/second-chance/typings';
import { useBreakpoints } from '@hooks/useBreakpoints';
import { useTranslation } from '@i18n/useTranslation';
import { ReactElement, useEffect, useState } from 'react';

import { useConfigurator } from '../ConfiguratorContext';
import ConfiguratorSummary from '../ConfiguratorSummary';
import { fetchConfiguratorDetails } from './actions/fetchConfiguratorDetails';
import AlternativeCard from './AlternativeCard';
import Header from './Header';

export default function AlternativesOverlay({
  secondChanceProduct
}: {
  secondChanceProduct?: SecondChanceProduct;
}) {
  const { trackEvent } = useAnalytics();
  const { t } = useTranslation('webshop.productPage.configurator');
  const modalTranslations = useFreddyTranslations('modal');
  const drawerTranslations = useFreddyTranslations('drawer');
  const { isMobile } = useBreakpoints();

  const [loading, setLoading] = useState(false);

  const {
    configurator,
    mainProduct,
    states: { selectedUseCaseIndex, selectedProductSlot, configuratorValues },
    actions: {
      setSelectedProductSlot,
      updateConfiguratorValue,
      updateConfiguratorDetails,
      getProductByUseCaseAndSlot,
      areAlternativesLoadedForUseCase,
      getProductAlternativeById,
      selectAlternative,
      getSelectedAlternativeByUseCaseAndSlot
    }
  } = useConfigurator();

  useEffect(() => {
    if (selectedProductSlot === undefined) return;
    if (areAlternativesLoadedForUseCase(selectedUseCaseIndex)) return;

    setLoading(true);

    fetchConfiguratorDetails({
      productId: mainProduct.id,
      useCaseId: configurator.useCases[selectedUseCaseIndex].id
    })
      .then(fetchedData => {
        if (fetchedData) {
          updateConfiguratorDetails(fetchedData);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    configurator.useCases,
    mainProduct.id,
    selectedUseCaseIndex,
    selectedProductSlot,
    updateConfiguratorDetails,
    areAlternativesLoadedForUseCase
  ]);

  if (selectedProductSlot === undefined) {
    return null;
  }

  const open = selectedProductSlot !== undefined;

  const onClose = () => {
    setSelectedProductSlot(undefined);

    const params = {
      sub_feature: 'configurator_overlay' as const
    };
    trackEvent(closeOverlayTrigger(params));
  };

  const trackOptionClick = (selectedProductId: string) => {
    const { products } = configurator.useCases[selectedUseCaseIndex].slots[selectedProductSlot];
    const params = {
      sub_feature: 'configurator_overlay' as const,
      index: products.findIndex(product => product?.id === Number(selectedProductId)) + 1,
      total_indices: products.length,
      item_id: Number(selectedProductId)
    };

    trackEvent(addItemTrigger(params));
  };

  const handleOptionClick = (selectedProductId: string) => {
    const selectedProduct = configurator.useCases[selectedUseCaseIndex].slots[
      selectedProductSlot
    ].products.find(product => product?.id === Number(selectedProductId));

    if (!selectedProduct) return;

    trackOptionClick(selectedProductId);

    selectAlternative({
      useCaseIndex: selectedUseCaseIndex,
      slotIndex: selectedProductSlot,
      productId: selectedProduct.id
    });

    const updatedConfiguratorValues = (configuratorValues.get(selectedUseCaseIndex) || []).map(
      item => ({
        ...item
      })
    );
    updateConfiguratorValue(updatedConfiguratorValues);
  };

  const productType = getProductByUseCaseAndSlot({
    useCaseIndex: selectedUseCaseIndex,
    slotIndex: selectedProductSlot
  })?.productType?.singularName;

  function renderAlternativeCard(product: { id: number } | null) {
    if (!product) return null;

    const productAlternative = getProductAlternativeById(product.id);

    if (!productAlternative) return null;

    return (
      <AlternativeCard
        key={product.id}
        product={productAlternative}
        discount={selectedSlot.discountPercentage}
      />
    );
  }

  const Body = (): ReactElement | null => {
    if (loading) return <Loader />;
    if (selectedProductSlot === undefined) return null;

    const selectedSlot = configurator.useCases[selectedUseCaseIndex].slots[selectedProductSlot];

    const selectedAlternativeId = getSelectedAlternativeByUseCaseAndSlot({
      useCaseIndex: selectedUseCaseIndex,
      slotIndex: selectedProductSlot
    });

    return (
      <Box>
        {!isMobile && (
          <Box>
            <Header />
            <Typography variant='h3' mb={400}>
              {t('selectAlternative', { productType })}
            </Typography>
          </Box>
        )}
        {isMobile && (
          <Box mb={400} mt={300}>
            <Typography variant='h3' mb={0}>
              {t('recommendedAccessories')}
            </Typography>
            <Typography mb={0}>{selectedSlot.description}</Typography>
          </Box>
        )}
        {loading ? (
          <Loader />
        ) : (
          <SelectableBoxGroup
            inputPlacement='floating'
            name='alternativesOverlay'
            spacing='default'
            type='radio'
            onChange={value => handleOptionClick(value)}
            value={selectedAlternativeId?.id.toString()}
          >
            <Grid gap={400}>{selectedSlot.products.map(renderAlternativeCard)}</Grid>
          </SelectableBoxGroup>
        )}
      </Box>
    );
  };

  const selectedSlot = configurator.useCases[selectedUseCaseIndex].slots[selectedProductSlot];

  return (
    <>
      {isMobile ? (
        <Drawer
          header={
            <>
              <DrawerHeader
                title={t('selectAlternative', { productType })}
                closeButtonProps={{
                  'aria-label': 'Dismiss'
                }}
              />
              <Box p={500}>
                <Header />
              </Box>

              <Divider />
            </>
          }
          open={open}
          placement='left'
          translations={drawerTranslations}
          onClose={onClose}
          body={
            <DrawerBody>
              <Body />
            </DrawerBody>
          }
          footer={
            <StyledFooter>
              <ConfiguratorSummary
                product={mainProduct}
                type='overlay'
                quantitySelectorIndex={
                  selectedSlot.quantitySelector.isEnabled ? selectedProductSlot : undefined
                }
                secondChanceProduct={secondChanceProduct}
              />
            </StyledFooter>
          }
        />
      ) : (
        <Modal
          open={open}
          onClose={onClose}
          title={t('otherOptions')}
          size='xlarge'
          translations={modalTranslations}
          fullHeight
          body={
            <ModalBody>
              <Body />
            </ModalBody>
          }
          footer={
            <StyledFooter>
              <ConfiguratorSummary
                product={mainProduct}
                type='overlay'
                quantitySelectorIndex={
                  selectedSlot.quantitySelector.isEnabled ? selectedProductSlot : undefined
                }
                secondChanceProduct={secondChanceProduct}
              />
            </StyledFooter>
          }
        />
      )}
    </>
  );
}

const StyledFooter = styled(ModalFooter)<ModalFooterProps | DrawerFooterProps>`
  display: flex;
  padding: ${({ theme }) => theme.spacing[200]} ${({ theme }) => theme.spacing[500]};

  ${({ theme }) => theme.breakpoint.minWidth('md')} {
    align-items: center;
    justify-content: space-between;
  }

  ${({ theme }) => theme.breakpoint.maxWidth('md')} {
    flex-direction: column;
  }
`;
